"use client";

import type { NimbleAtsJobId } from "@nimble/datamodel/types/nimbleAtsJobId";
import { useUrlDrawer } from "@nimble/react-state-hooks/use-url-drawer";
import { Zap } from "lucide-react";
import { useCallback } from "react";

import SignInButtonWrapper from "@/app/(discover)/components/sign-in-button-wrapper";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import { QuickApplyConfirmationModalContainer } from "@/components/shared/dialogs/quick-apply-modal/quick-apply-modal.container";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTracking } from "@/hooks/use-tracking";
import { jobViewActionsTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import type { User } from "@/lib/types/auth";

const QUICK_APPLY_BUTTON_CLASSNAME =
  "h-12 w-full rounded-xl bg-gradient-to-r from-accent-purple to-info hover:from-accent-purple-hover hover:to-info-hover text-base font-semibold text-primary-foreground hover:opacity-90";

interface QuickApplyButtonProps {
  user: User;
  jobId: string;
  nimbleAtsJobId: NimbleAtsJobId;
  jobUrl: string;
}

interface QuickApplyButtonViewProps {
  jobId: string;
  onClick: () => void;
}

function QuickApplyButtonView({ jobId, onClick }: QuickApplyButtonViewProps) {
  return (
    <Button
      type="button"
      className={QUICK_APPLY_BUTTON_CLASSNAME}
      onClick={onClick}
      aria-label="Quick apply to this job using your Nimble profile"
      data-testid={`${jobViewActionsTestIds.JOB_VIEW_ACTIONS_QUICK_APPLY_BUTTON}-${jobId}`}
    >
      <Zap className="h-4 w-4" />
      Quick Apply
    </Button>
  );
}

export function QuickApplyButton({
  user,
  jobId,
  nimbleAtsJobId,
  jobUrl,
}: QuickApplyButtonProps) {
  const { value, openDrawer, closeDrawer } = useUrlDrawer({
    paramName: "quickApply",
  });
  const { trackQuickApplyClicked } = useTracking();

  const isDialogOpen = value === jobId;

  const handleOpenDialog = useCallback(() => {
    trackQuickApplyClicked({ jobId });
    openDrawer(jobId);
  }, [openDrawer, jobId, trackQuickApplyClicked]);

  return (
    <>
      <QuickApplyButtonView jobId={jobId} onClick={handleOpenDialog} />
      {isDialogOpen && (
        <QuickApplyConfirmationModalContainer
          user={user}
          nimbleAtsJobId={nimbleAtsJobId}
          jobUrl={jobUrl}
          isOpen={isDialogOpen}
          onClose={closeDrawer}
        />
      )}
    </>
  );
}

const QuickApplyButtonWithGate = withAuthGate(QuickApplyButton, {
  customLoader: <Skeleton className="h-12 w-full rounded-xl" />,
  customPaywall: () => (
    <SignInButtonWrapper
      variant="default"
      className={QUICK_APPLY_BUTTON_CLASSNAME}
      label="Sign in to Quick Apply"
      testId={jobViewActionsTestIds.JOB_VIEW_ACTIONS_QUICK_APPLY_SIGN_IN}
    />
  ),
});

export default QuickApplyButtonWithGate;
